'use client';

import {
  type Context,
  type DependencyList,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useSyncExternalStore,
} from 'react';

/**
 * Create a constant value that only updates when dependencies change.
 * Useful for creating stable references to expensive computations.
 */
export function useConstant<T>(factory: () => T, deps: DependencyList = []): T {
  const mountRef = useRef(false);
  const valueRef = useRef<T | undefined>(undefined);
  if (valueRef.current === undefined) {
    valueRef.current = factory();
  }
  useEffect(() => {
    if (!mountRef.current) {
      mountRef.current = true;
    } else {
      valueRef.current = factory();
    }
  }, deps);

  return valueRef.current;
}

/**
 * Use a context with type safety, throwing if used outside provider.
 * Ensures context is never null/undefined when accessed.
 */
export function useStrictContext<T>(context: Context<T | null>): T {
  const result = useContext(context);
  if (result) {
    return result;
  } else {
    throw new Error('useStrictContext must be used within the corresponding context provider');
  }
}

// biome-ignore lint/suspicious/noEmptyBlockStatements: empty subscribe function
const emptySubscribe = () => () => {};

/**
 * Returns a stable callback that reports whether the component is still mounted.
 * Used to guard against state updates after unmount.
 */
export function useIsMounted(): () => boolean {
  const isMountedRef = useRef(false);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  return useCallback(() => isMountedRef.current, []);
}

/**
 * Determine if a component has been hydrated at the earliest possible time.
 * This is a legitimate use of the React useSyncExternalStore.
 * See the first link below for details and usage examples.
 *
 * @see https://tkdodo.eu/blog/avoiding-hydration-mismatches-with-use-sync-external-store
 * @see https://react.dev/reference/react/useSyncExternalStore
 * @returns true for post-hydration on client side, false on server.
 */
export function useIsHydrated() {
  return useSyncExternalStore(
    emptySubscribe,
    () => true,
    () => false,
  );
}
